import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { CategoryData, ServiceItem } from "@/types";
import { toast } from "sonner";

// Unified hook for fetching and managing services data
export const useServicesData = () => {
  const queryClient = useQueryClient();

  const {
    data: servicesData,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ["categories"],
    queryFn: async () => {
      // Fetch categories
      const { data: categoriesData, error: categoriesError } = await supabase
        .from("categories")
        .select("*")
        .order("display_order");

      if (categoriesError) throw categoriesError;

      // Fetch services
      const { data: servicesData, error: servicesError } = await supabase
        .from("services")
        .select("*")
        .order("display_order");

      if (servicesError) throw servicesError;

      // Process categories and services into unified structure
      const categoriesMap: Record<string, CategoryData> = {};
      
      if (categoriesData) {
        categoriesData.forEach((category) => {
          categoriesMap[category.id] = {
            ...category,
            services: [],
          };
        });
      }

      if (servicesData) {
        servicesData.forEach((service) => {
          const categoryId = service.category_id;
          if (categoriesMap[categoryId]) {
            categoriesMap[categoryId].services.push(service);
          }
        });
      }

      return {
        categories: categoriesMap,
        services: servicesData || [],
      };
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    retry: 2,
    retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, 30000),
  });

  // Improved reordering mutation with compact and sequential logic
  const reorderServiceMutation = useMutation({
    mutationFn: async ({
      id,
      direction,
      categoryId,
    }: {
      id: string;
      direction: "up" | "down";
      categoryId: string;
    }) => {
      if (!servicesData) throw new Error("Services data not loaded");

      // Get all services in the category, sorted by display_order
      const categoryServices = servicesData.services
        .filter((s) => s.category_id === categoryId)
        .sort((a, b) => (a.display_order || 0) - (b.display_order || 0));

      const currentIndex = categoryServices.findIndex((s) => s.id === id);
      if (currentIndex === -1) throw new Error("Service not found");

      // Calculate new index
      const newIndex = direction === "up" ? currentIndex - 1 : currentIndex + 1;
      
      // Check bounds
      if (newIndex < 0 || newIndex >= categoryServices.length) {
        return; // No change needed
      }

      // Create new order array by moving the service
      const reorderedServices = [...categoryServices];
      const [movedService] = reorderedServices.splice(currentIndex, 1);
      reorderedServices.splice(newIndex, 0, movedService);

      // Update all services with new sequential order (1, 2, 3, ...)
      const updates = reorderedServices.map((service, index) => ({
        id: service.id,
        display_order: index + 1,
      }));

      // Batch update all services
      for (const update of updates) {
        const { error } = await supabase
          .from("services")
          .update({ display_order: update.display_order })
          .eq("id", update.id);

        if (error) throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["categories"] });
      toast.success("Ordre des services mis à jour");
    },
    onError: (error) => {
      console.error("Error reordering service:", error);
      toast.error(`Erreur lors de la réorganisation: ${error.message}`);
    },
  });

  // Similar logic for category reordering
  const reorderCategoryMutation = useMutation({
    mutationFn: async ({
      id,
      direction,
    }: {
      id: string;
      direction: "up" | "down";
    }) => {
      if (!servicesData) throw new Error("Categories data not loaded");

      // Get all categories sorted by display_order
      const allCategories = Object.values(servicesData.categories).sort(
        (a, b) => (a.display_order || 0) - (b.display_order || 0)
      );

      const currentIndex = allCategories.findIndex((c) => c.id === id);
      if (currentIndex === -1) throw new Error("Category not found");

      // Calculate new index
      const newIndex = direction === "up" ? currentIndex - 1 : currentIndex + 1;
      
      // Check bounds
      if (newIndex < 0 || newIndex >= allCategories.length) {
        return; // No change needed
      }

      // Create new order array by moving the category
      const reorderedCategories = [...allCategories];
      const [movedCategory] = reorderedCategories.splice(currentIndex, 1);
      reorderedCategories.splice(newIndex, 0, movedCategory);

      // Update all categories with new sequential order
      const updates = reorderedCategories.map((category, index) => ({
        id: category.id,
        display_order: index + 1,
      }));

      // Batch update all categories
      for (const update of updates) {
        const { error } = await supabase
          .from("categories")
          .update({ display_order: update.display_order })
          .eq("id", update.id);

        if (error) throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["categories"] });
      toast.success("Ordre des catégories mis à jour");
    },
    onError: (error) => {
      console.error("Error reordering category:", error);
      toast.error(`Erreur lors de la réorganisation: ${error.message}`);
    },
  });

  return {
    categories: servicesData?.categories || {},
    services: servicesData?.services || [],
    isLoading,
    error,
    refetch,
    reorderService: reorderServiceMutation.mutate,
    reorderCategory: reorderCategoryMutation.mutate,
    isReorderingService: reorderServiceMutation.isPending,
    isReorderingCategory: reorderCategoryMutation.isPending,
  };
};